/**
 * Fetch helpers for the SEO-tracking admin page.
 *
 * Data is baked at git-push time by
 * `seo-tracking/scripts/build-admin-index.mjs` and committed to
 * `admin/public/seo-tracking-index.json` + `admin/public/seo-tracking/{id}.json`.
 * No backend Lambda — admin frontend fetches static assets from the
 * admin S3 bucket directly.
 */

export type Indexing = 'PASS' | 'NEUTRAL' | 'FAIL' | 'UNKNOWN' | string;

export type IndexRow = {
  id: string;                   // "blog/ipfs-cid-explained/en"
  section: 'landing' | 'blog' | 'docs';
  slug: string;
  locale: string;
  url: string | null;
  title: string | null;
  createdDate?: string | null;   // "YYYY-MM-DD" — from frontmatter, git, or reference-date fallback
  updatedDate?: string | null;
  gsc: {
    date: string;
    clicks: number;
    impressions: number;
    ctr: number;                // 0..1
    position: number;
    indexing: Indexing;
    deltaCtrPp: number | null;
    deltaPosition: number | null;
    deltaIndexing: string;
  } | null;
  audit: {
    issuesCount: number;
    firstIssue: string | null;
  };
  todos: {
    openP0: number;
    openP1: number;
    openP2: number;
    inProgress: number;
    needsResuggest: number;       // applier-rejected, awaiting next suggest cycle
    watchList: number;            // borderline signals awaiting check-back date
    closedImproved: number;
    closedWorsened: number;
    closedNeutral: number;
    closedOther: number;
  };
  relatedSiteTodosCount?: number;
};

export type SeoTrackingIndex = {
  version: number;
  generatedAt: string;
  pageCount: number;
  pages: IndexRow[];
};

export type GscHistoryEntry = {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  indexing: Indexing;
};

export type TopQuery = {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

export type OnPageCheck = {
  ok: boolean;
  label: string;
  value: string;
};

export type TodoItem = { title: string; body: string };
export type InProgressItem = { date: string; priority: string; patternId: string; title: string; body: string };
export type ClosedItem = { date: string; outcome: string; patternId: string | null; title: string; body: string };
export type NeedsResuggestItem = { priority: string; rejectedDate: string; title: string; reason: string; body: string };
export type WatchListItem = {
  priority: string;
  failCount: number;
  title: string;
  metric: string | null;
  promoteIf: string | null;
  checkback: string | null;
  body: string;
};
export type RelatedSiteTodo = { id: string; title: string; status: string };

export type PageDetail = {
  id: string;
  url: string | null;
  title: string | null;
  section: string;
  locale: string;
  lastAuditDate: string | null;
  createdDate?: string | null;
  createdSource?: string | null;   // "frontmatter" | "git-first-commit" | "reference-date"
  updatedDate?: string | null;
  updatedSource?: string | null;
  gscHistory: GscHistoryEntry[];
  topQueries: TopQuery[];
  onPageChecks: OnPageCheck[];
  issues: string[];
  todos: {
    open: { P0: TodoItem[]; P1: TodoItem[]; P2: TodoItem[] };
    inProgress: InProgressItem[];
    needsResuggest: NeedsResuggestItem[];
    watchList: WatchListItem[];
    closed: ClosedItem[];
  };
  relatedSiteTodos?: RelatedSiteTodo[];
  sources: { audit: string; todo: string };
};

export async function fetchIndex(): Promise<SeoTrackingIndex> {
  const r = await fetch('/seo-tracking-index.json', { cache: 'no-cache' });
  if (!r.ok) throw new Error(`fetchIndex: ${r.status}`);
  return r.json();
}

export async function fetchPageDetail(id: string): Promise<PageDetail> {
  const r = await fetch(`/seo-tracking/${id}.json`, { cache: 'no-cache' });
  if (!r.ok) throw new Error(`fetchPageDetail ${id}: ${r.status}`);
  return r.json();
}
